import { ApiError, NetworkError, STATUS_CSRF_MISMATCH } from './api'

/**
 * Turns anything thrown by the client into a sentence worth showing.
 *
 * The API writes its own messages for humans — Laravel-shaped validation text,
 * domain refusals like "Cannot delete the default project.", upgrade prompts —
 * so the rule is to show what the server said and only invent copy when there
 * is nothing to show.
 */
export function messageFor(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof NetworkError) {
    return 'Could not reach Taskly. Check your connection and try again.'
  }
  if (error instanceof ApiError) {
    if (error.status >= 500) return 'The server ran into a problem. Please try again.'
    if (error.status === STATUS_CSRF_MISMATCH) {
      // The client already retried once with a fresh token. Saying "CSRF token
      // mismatch" to a person is useless; the recovery is to try again.
      return 'Your session was refreshed. Please try that again.'
    }
    return error.message || fallback
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/** The first validation message for a field, if this error carries one. */
export function fieldErrorFor(error: unknown, field: string): string | undefined {
  return error instanceof ApiError ? error.fieldError(field) : undefined
}

/**
 * True when the error is a plain-message failure with no field to attach it to,
 * so a form should show it above the submit button rather than inline.
 */
export function isFormLevel(error: unknown, fields: string[]): boolean {
  if (!error) return false
  if (!(error instanceof ApiError)) return true
  return !fields.some((field) => error.fieldError(field))
}
